import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import ReportModal from 'components/moderation/ReportModal';
import ModerationOverflowMenu from 'components/moderation/ModerationOverflowMenu';
import { moderationService } from 'api/services/ModerationService';
import { ReportContentType } from 'api/types/moderation';
import { sellerService } from 'api/services/SellerService';
import type {
  SellerProfileViewModel,
  SellerRatingViewModel,
  SellerListingCardViewModel,
} from 'api/types/seller';
import type { PaginatedList } from 'api/types/common/PaginatedList';
import AdListGridItem from 'components/AdList/AdListGridItem';
import Navbar from 'components/Header/Navbar';
import Footer from 'components/Footer';
import { sellerListingCardToAdListing } from 'api/utils/listingMapper';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const RATINGS_PAGE_SIZE = 10;
const LISTINGS_PAGE_SIZE = 12;

const PLACEHOLDER_AVATAR =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23adb5bd"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';
const formatDateTr = (iso?: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('tr-TR');
};

const locationLabel = (city?: string | null, district?: string | null): string => {
  const parts = [district, city].filter((p): p is string => !!p && p.trim() !== '');
  return parts.join(', ');
};

/**
 * Renders five filled / half / empty stars for a 0–5 rating value.
 */
const Stars: React.FC<{ value: number }> = ({ value }) => {
  const icons: string[] = [];
  for (let i = 1; i <= 5; i++) {
    if (value >= i) icons.push('fas fa-star text-warning');
    else if (value >= i - 0.5) icons.push('fas fa-star-half-alt text-warning');
    else icons.push('far fa-star text-warning');
  }
  return (
    <span>
      {icons.map((c, idx) => (
        <i key={idx} className={c} />
      ))}
    </span>
  );
};

const SaticiProfil: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const profileUserId = id ?? '';
  const isGuidProfile = GUID_RE.test(profileUserId);

  const [profile, setProfile] = useState<SellerProfileViewModel | null | undefined>(undefined);
  const [ratings, setRatings] = useState<PaginatedList<SellerRatingViewModel> | null>(null);
  const [listings, setListings] = useState<PaginatedList<SellerListingCardViewModel> | null>(null);
  const [ratingsPage, setRatingsPage] = useState(1);
  const [listingsPage, setListingsPage] = useState(1);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [listingsLoading, setListingsLoading] = useState(false);

  const showProfileModeration = isGuidProfile && !!user?.id && user.id !== profileUserId;

  useEffect(() => {
    let cancelled = false;
    if (!isGuidProfile) {
      setProfile(null);
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      const response = await sellerService.getProfile(profileUserId);
      if (cancelled) return;
      setProfile(response.isSuccess ? response.data : null);
    })();
    return () => {
      cancelled = true;
    };
  }, [profileUserId, isGuidProfile]);

  const loadRatings = useCallback(
    async (page: number) => {
      if (!isGuidProfile) return;
      setRatingsLoading(true);
      try {
        const response = await sellerService.getRatings(
          profileUserId,
          page,
          RATINGS_PAGE_SIZE,
        );
        if (response.isSuccess && response.data) setRatings(response.data);
      } finally {
        setRatingsLoading(false);
      }
    },
    [profileUserId, isGuidProfile],
  );

  const loadListings = useCallback(
    async (page: number) => {
      if (!isGuidProfile) return;
      setListingsLoading(true);
      try {
        const response = await sellerService.getListings(
          profileUserId,
          page,
          LISTINGS_PAGE_SIZE,
        );
        if (response.isSuccess && response.data) setListings(response.data);
      } finally {
        setListingsLoading(false);
      }
    },
    [profileUserId, isGuidProfile],
  );

  useEffect(() => {
    if (profile) {
      loadRatings(ratingsPage);
    }
  }, [profile, ratingsPage, loadRatings]);

  useEffect(() => {
    if (profile) {
      loadListings(listingsPage);
    }
  }, [profile, listingsPage, loadListings]);

  const ratingAverageLabel = useMemo(() => {
    if (!profile) return '0.0';
    return profile.ratingAverage.toFixed(1);
  }, [profile]);

  if (profile === undefined) {
    return <div className="container py-5 text-center">Satıcı bilgileri yükleniyor...</div>;
  }

  if (profile === null) {
    return <div className="container py-5 text-center">Satıcı bulunamadı</div>;
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="container py-4">
        {/* Satıcı Profil Kartı */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-2 text-center">
                <img
                  alt="Profil"
                  className="rounded-circle mb-3"
                  height="120"
                  src={profile.avatarUrl || PLACEHOLDER_AVATAR}
                  width="120"
                />
              </div>
              <div className="col-md-7">
                <h2 className="mb-2">{profile.displayName}</h2>
                {locationLabel(profile.city, profile.district) && (
                  <p className="text-muted mb-2">
                    <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                    {locationLabel(profile.city, profile.district)}
                  </p>
                )}
                <p className="text-muted mb-2">
                  <i className="fas fa-user me-2 text-primary"></i>Üye Olma: {formatDateTr(profile.joinDate)}
                </p>
                <div className="d-flex align-items-center mb-2">
                  <div className="me-3">
                    <Stars value={profile.ratingAverage} />
                    <span className="ms-2">{ratingAverageLabel}</span>
                    <span className="text-muted ms-2">({profile.ratingCount} değerlendirme)</span>
                  </div>
                </div>
              </div>
              <div className="col-md-3 text-end">
                <div className="mb-3">
                  <h4 className="mb-0">{profile.activeListingCount}</h4>
                  <small className="text-muted">Aktif İlan</small>
                </div>
                <div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
                  {showProfileModeration && (
                    <ModerationOverflowMenu
                      dropdownId="satici-profil-mod-dd"
                      onReportClick={() => {
                        const el = document.getElementById('profileReportModal');
                        const M = (window as unknown as { bootstrap?: { Modal: { getOrCreateInstance: (e: Element) => { show: () => void } } } })
                          .bootstrap?.Modal;
                        if (el && M) M.getOrCreateInstance(el).show();
                      }}
                      onBlockClick={async () => {
                        if (!isAuthenticated) {
                          navigate('/giris');
                          return;
                        }
                        if (!window.confirm('Bu kullanıcıyı engellemek istediğinize emin misiniz?')) return;
                        const res = await moderationService.blockUser({ blockedUserId: profileUserId });
                        window.alert(res.message ?? (res.isSuccess ? 'Kullanıcı engellendi.' : 'İşlem başarısız.'));
                      }}
                    />
                  )}
                </div>
                {showProfileModeration && (
                  <ReportModal
                    modalId="profileReportModal"
                    contentType={ReportContentType.Profile}
                    reportedUserId={profileUserId}
                    isAuthenticated={isAuthenticated}
                    isOwnContent={false}
                    onRequireLogin={() => navigate('/giris')}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Hakkında Bölümü */}
        {profile.description && profile.description.trim() !== '' && (
          <div className="card mb-4">
            <div className="card-body">
              <h4 className="card-title mb-3">Hakkında</h4>
              <p className="card-text">{profile.description}</p>
            </div>
          </div>
        )}

        {/* Aktif İlanlar */}
        <h3 className="mb-3">Aktif İlanlar</h3>
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mb-2">
          {listings && listings.items.length > 0 ? (
            listings.items.map((listing) => (
              <AdListGridItem
                key={listing.listingId}
                ilan={sellerListingCardToAdListing(listing)}
                displayFavorites={false}
              />
            ))
          ) : (
            !listingsLoading && (
              <div className="col-12 text-center">
                <p className="text-muted">Aktif ilan bulunamadı</p>
              </div>
            )
          )}
        </div>
        {listings && listings.totalPages > 1 && (
          <div className="d-flex justify-content-center gap-2 mb-4">
            <Button
              className="btn btn-outline-primary"
              disabled={listingsPage <= 1 || listingsLoading}
              onClick={() => setListingsPage((p) => Math.max(1, p - 1))}
            >
              <i className="fas fa-chevron-left me-1"></i> Önceki
            </Button>
            <span className="align-self-center text-muted">
              Sayfa {listings.page} / {listings.totalPages}
            </span>
            <Button
              className="btn btn-outline-primary"
              disabled={listingsPage >= listings.totalPages || listingsLoading}
              onClick={() => setListingsPage((p) => p + 1)}
            >
              Sonraki <i className="fas fa-chevron-right ms-1"></i>
            </Button>
          </div>
        )}

        {/* Değerlendirmeler */}
        <h3 className="mb-4">Değerlendirmeler</h3>
        <div className="card mb-4">
          <div className="card-body">
            <div className="row mb-4">
              <div className="col-md-6">
                <h4 className="mb-3">Genel Puan</h4>
                <div className="d-flex align-items-center">
                  <div className="display-4 me-3">{ratingAverageLabel}</div>
                  <div>
                    <div className="mb-1">
                      <Stars value={profile.ratingAverage} />
                    </div>
                    <small className="text-muted">{profile.ratingCount} değerlendirme</small>
                  </div>
                </div>
              </div>
            </div>

            <div>
              {ratings && ratings.items.length > 0 ? (
                ratings.items.map((r) => (
                  <div key={r.ratingId} className="border-bottom pb-3 mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <div>
                        <span className="fw-bold">{r.raterDisplayName}</span>
                      </div>
                      <div>
                        <Stars value={r.stars} />
                      </div>
                    </div>
                    {r.comment && <p className="mb-2">{r.comment}</p>}
                    <small className="text-muted">{formatDateTr(r.createdAt)}</small>
                  </div>
                ))
              ) : (
                !ratingsLoading && (
                  <p className="text-muted text-center">Henüz değerlendirme yok</p>
                )
              )}
            </div>

            {ratings && ratings.totalPages > 1 && (
              <div className="pagination-container py-4 border-top">
                <nav aria-label="Değerlendirme sayfaları">
                  <ul className="pagination justify-content-center mb-0">
                    <li className={`page-item ${ratingsPage <= 1 ? 'disabled' : ''}`}>
                      <Button
                        className="page-link"
                        disabled={ratingsPage <= 1 || ratingsLoading}
                        onClick={() => setRatingsPage((p) => Math.max(1, p - 1))}
                      >
                        <i className="fas fa-chevron-left me-1"></i> Önceki
                      </Button>
                    </li>
                    <li className="page-item active">
                      <Button className="page-link">
                        {ratings.page} / {ratings.totalPages}
                      </Button>
                    </li>
                    <li className={`page-item ${ratingsPage >= ratings.totalPages ? 'disabled' : ''}`}>
                      <Button
                        className="page-link"
                        disabled={ratingsPage >= ratings.totalPages || ratingsLoading}
                        onClick={() => setRatingsPage((p) => p + 1)}
                      >
                        Sonraki <i className="fas fa-chevron-right ms-1"></i>
                      </Button>
                    </li>
                  </ul>
                </nav>
                <div className="text-center text-muted mt-3">
                  <small>
                    Toplam <strong>{ratings.totalCount}</strong> değerlendirme
                  </small>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* CSS ekleyelim */}
      <style>
        {`
        /* Pagination Styles */
        .pagination-container {
          background-color: var(--sb-bg-muted);
          border-color: var(--sb-border) !important;
        }
        
        .pagination .page-link {
          color: var(--sb-text);
          background-color: var(--sb-bg);
          border-color: var(--sb-border);
          border-radius: 8px;
          margin: 0 4px;
          padding: 12px 20px;
          transition: all 0.3s ease;
          font-weight: 600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
        }
        
        .pagination .page-link:hover {
          background-color: var(--sb-bg-muted);
          border-color: var(--sb-border);
          transform: translateY(-3px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
          color: #0d6efd;
        }
        
        .pagination .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
          color: white;
          transform: translateY(-3px);
          box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
        }
        
        .pagination .page-item.disabled .page-link {
          color: var(--sb-text-muted);
          pointer-events: none;
          background-color: var(--sb-bg);
          border-color: var(--sb-border);
          opacity: 0.6;
        }
        
        .pagination-lg .page-link {
          font-size: 1rem;
        }
        
        @media (max-width: 576px) {
          .pagination .page-link {
            padding: 8px 12px;
            font-size: 0.9rem;
          }
        }
        `}
      </style>

      <Footer />
    </div>
  );
};

export default SaticiProfil;
